<template>
  <q-table
    ref="table"
    :pagination="currentPagination"
    :rows="filteredRows"
    :columns="visibleColumns"
    :row-key="rowKey"
    :selection="selection"
    :selected="selected"
    flat
    bordered
    :loading="loading"
    :table-row-class-fn="getRowClass"
    :virtual-scroll="virtualScroll"
    :virtual-scroll-item-size="48"
    :virtual-scroll-sticky-size-start="48"
    :table-style="{ maxHeight: maxHeight }"
    @update:pagination="onSetPagination"
    @update:selected="onSelectRows"
  >
    <template #top>
      <div class="full-width">
        <!-- Top-aligned, so the actions stay level with the first chip row once the chips wrap. -->
        <div class="row items-start q-col-gutter-xs">
          <template v-if="!isSearching">
            <div class="row col items-center q-col-gutter-xs">
              <slot name="top" />
              <filter-chips-bar
                v-if="!hideFilterChips"
                :filters="filters"
                :filter-criteria="filterCriteria"
                :filter-groups="columnGroupLabels"
                :hide-add="!canAddFilter"
                @update:filters="onFiltersChanged"
              />
            </div>
          </template>
          <div
            v-if="!disableSearch"
            :class="{
              'col': isSearching,
              'col-auto': !isSearching,
              'q-pl-sm': !isSearching,
            }"
          >
            <search-input
              ref="searchInput"
              v-model="searchTerm"
              input-class="col"
              data-e2e="fields-search-table"
              @show-input="onShowSearch"
              @blur-input="onHideSearch"
              @hide-input="onHideSearch"
            />
          </div>
          <div v-if="!disableExport">
            <q-btn
              icon="ios_share"
              color="primary"
              flat
              round
              dense
              :disable="filteredRows.length === 0"
              menu-self="top middle"
            >
              <q-menu>
                <q-list style="min-width: 260px">
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onExport('excel')"
                  >
                    <q-item-section avatar>
                      <q-icon
                        color="dark"
                        name="fas fa-file-excel"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('common.tableExportToExcel') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onExport('csv')"
                  >
                    <q-item-section avatar>
                      <q-icon
                        color="dark"
                        name="fas fa-file-csv"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('common.tableExportToCSV') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
          <table-views
            v-if="!disableViews"
            :context="saveFilterId+'Views'"
            :filters="filters"
            :pagination="currentPagination"
            :columns="selectedColumns"
            :other-settings="otherSettings"
            :active-view-id="activeViewId"
            @load-view="onLoadView"
            @update:active-view-id="$emit('update:activeViewId', $event)"
          />
          <slot name="top-menu" />
        </div>
      </div>
    </template>
    <template
      v-for="(index, name) in inheritedSlots"
      #[name]="data"
    >
      <slot
        :name="name"
        v-bind="data"
      />
    </template>
    <template #header-cell-actions="props">
      <slot
        name="custom-actions-header"
        v-bind="props"
      >
        <q-th :props="props">
          <q-btn
            flat
            round
            dense
            size="sm"
            color="dark-variant"
            icon="settings"
            @click="onSelectColumns"
          />
        </q-th>
      </slot>
    </template>
    <template #header-selection="scope">
      <q-checkbox
        :model-value="scope.selected"
        data-e2e="action-bulk-select-all-rows"
        @update:model-value="onSelectAllRows"
      />
    </template>
    <template #body-selection="props">
      <universal-tooltip
        :show-tooltip="isRowDisabled(props.row) && !!getDisabledRowTooltip(props.row)"
        :text="getDisabledRowTooltip(props.row)"
      >
        <q-checkbox
          v-model="props.selected"
          :disable="isRowDisabled(props.row)"
        />
      </universal-tooltip>
    </template>
  </q-table>
</template>
<script>
import FilterChipsBar from 'components/UI/FilterChipsBar.vue';
import SearchInput from 'components/UI/SearchInput';
import TableViews from 'components/UI/TableViews';
import UniversalColumnSelector from 'components/UI/UniversalColumnSelector';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import { chain, filter, isArray, isEmpty, omit } from 'lodash';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Interactions from 'src/mixins/Interactions';
import TableMixin from 'src/mixins/TableMixin';
import { sortByArray } from 'src/utils/AppUtils';

export default {
  name: 'FilteredTable',

  components: {
    FilterChipsBar, SearchInput, TableViews, UniversalTooltip,
  },

  mixins: [FiltersMixin, Interactions, TableMixin],

  props: {
    columns: {
      type: Array,
      required: true,
    },

    rows: {
      type: Array,
      required: true,
    },

    disabledRows: {
      type: Array,
      default: () => ([]),
    },

    disabledRowTooltip: {
      type: String,
      default: '',
    },

    // Per-row disabled tooltip, when one message doesn't fit every row
    disabledRowTooltipFn: {
      type: Function,
      default: null,
    },

    foreignObjects: {
      type: Object,
      default: () => ({}),
    },

    defaultColumns: {
      type: Array,
      default: () => [],
    },

    defaultFilters: {
      type: Object,
      default: () => ({}),
    },

    pagination: {
      type: Object,
      default: () => ({
        descending: false,
        page: 1,
        rowsPerPage: 500,
      }),
    },

    saveFilterId: {
      type: String,
      default: null,
    },

    rowKey: {
      type: String,
      default: 'id',
    },
    selected: {
      type: Array,
      default: () => ([]),
    },
    selection: {
      type: String,
      default: 'none',
    },
    exportFilename: {
      type: String,
      default: 'export',
    },
    columnGroupLabels: {
      type: Array,
      default: () => ([]),
    },
    loading: {
      type: Boolean,
      default: false,
    },

    disableViews: {
      type: Boolean,
      default: false,
    },

    disableExport: {
      type: Boolean,
      default: false,
    },

    disableSearch: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: String,
      default: '60vh',
    },
    virtualScroll: {
      type: Boolean,
      default: true,
    },
    hideFilterChips: {
      type: Boolean,
      default: false,
    },
    otherSettings: {
      type: Object,
      default: () => ({}),
    },
    externalFilters: {
      type: Object,
      default: null,
    },
    externalSelectedColumns: {
      type: Array,
      default: null,
    },
    activeViewId: {
      type: String,
      default: null,
    },
  },

  emits: [
    'update:pagination', 'update:selected', 'update:selectedColumns', 'update:activeViewId',
    'load-view',
    // AFTER the file is written, never before: a page that has to record an export has to know it
    // actually happened, and a failed write must not look like a successful one. Carries the rows
    // that went into the file, because that is what a caller records — not what is on screen now.
    'exported',
  ],

  data() {
    return {
      isSearching: false,
      searchTerm: '',
      currentPagination: {},
      filters: {},
      selectedColumns: [],
    };
  },

  computed: {

    /*
     * Derived once per column change rather than per render. Called from the template it returned a
     * fresh array every time the parent re-rendered, so the chips bar saw a changed prop on each
     * one, and the appointments table builds over a hundred columns.
     */
    filterCriteria() {
      return this.getFilterCriteriaFromColumns(this.columns);
    }, // filterCriteria()

    canAddFilter() {
      return !chain(this.filterCriteria)
        .map((criteria) => criteria.value)
        .difference(Object.keys(this.filters))
        .isEmpty()
        .value();
    }, // canAddFilter()

    inheritedSlots() {
      return omit(this.$slots, ['top']);
    },

    filteredRows() {
      if (this.searchTerm === '') {
        return this.getFilteredElementsWithObjects(this.rows, this.filters, this.foreignObjects);
      }
      const formattedSearch = chain(this.searchTerm).toLower().trim().value();

      return filter(
        this.rows,
        (row) => (chain(this.visibleColumns)
          .map((column) => {
            const formatFunc = column.format || ((value) => value);
            return (String(formatFunc(row[column.field], row)).toLowerCase().includes(formattedSearch));
          })
          .some()
          .value()),
      );
    }, // filteredRows()

    visibleColumns() {
      return this.columns
        .filter((column) => this.selectedColumns.includes(column.name) || column.persistent)
        .concat({
          name: 'actions',
          label: '',
        }).sort(sortByArray({ arrayToSortBy: this.selectedColumns, searchKey: 'name' }));
    },

  },

  watch: {

    pagination: {
      handler() {
        this.currentPagination = this.pagination;
      },
      immediate: true,
      deep: true,
    },

    externalFilters: {
      handler(newVal) {
        if (newVal !== null) {
          this.filters = newVal;
        }
      },
      deep: true,
      immediate: true,
    },
    externalSelectedColumns: {
      handler(newVal) {
        if (newVal !== null) {
          this.selectedColumns = newVal;
        }
      },
      deep: true,
      immediate: true,
    },
  },

  async mounted() {
    this.loadSettings();
  },

  methods: {

    onSelectRows(selectedRows) {
      this.$emit('update:selected', selectedRows);
    },

    onSelectAllRows() {
      const selectedWithoutDisabled = this.selected.filter((row) => !this.isRowDisabled(row));
      const selectedDisabledOnly = this.selected.filter((row) => this.isRowDisabled(row));

      if (selectedWithoutDisabled.length === 0) {
        // No rows selected, so we select all rows
        const allRows = this.$refs.table.computedRows
          .filter((row) => !this.isRowDisabled(row))
          .concat(selectedDisabledOnly);
        this.$emit('update:selected', allRows);
      } else {
        // Some rows are selected, so we unselect all rows
        this.clearSelection();
      }
    }, // onSelectAllRows()

    onSetPagination(pagination) {
      this.currentPagination = pagination;
      this.$emit('update:pagination', this.currentPagination);
    },

    onFiltersChanged(filters) {
      this.clearSelection();
      this.filters = filters;
      this.saveFilter();
    }, // onFiltersChanged()

    onSelectColumns() {
      const selectionColumns = chain(this.columns)
        .reject({ hideFromSelection: true })
        .map((column) => (
          {
            value: column.name,
            label: column.selectionLabel || column.label || column.exportLabel || '',
            disable: column.mandatory || false,
            group: column.group || null,
          }
        ))
        .value();

      this.openComponentDialog(
        UniversalColumnSelector,
        {
          options: selectionColumns,
          optionGroupLabels: this.columnGroupLabels,
          selected: this.selectedColumns,
          title: this.$t('common.tableEditColumns'),
          isAbleToReorder: true,
        },
        (newSelectedColumns) => {
          this.selectedColumns = newSelectedColumns;
          this.saveSelectedColumns();
        },
      );
    }, // onSelectColumns()

    onShowSearch() {
      this.isSearching = true;
    }, // onShowSearch()

    onHideSearch() {
      this.isSearching = false;
    }, // onHideSearch()

    onLoadView(tableViewProps) {
      this.selectedColumns = tableViewProps.columns;
      this.onSetPagination(tableViewProps.pagination);
      this.filters = tableViewProps.filters;
      this.$emit('load-view', tableViewProps);
    }, // onLoadView()

    async onExport(type) {
      const typeToExtensionMap = {
        excel: 'xlsx',
        csv: 'csv',
      };

      this.$q.loading.show();
      try {
        const columnsToExport = this.columns
          .filter(({ name }) => this.selectedColumns.includes(name))
          .sort(sortByArray({ arrayToSortBy: this.selectedColumns, searchKey: 'name' }));
        await this.exportTableDataToSpreadsheet(
          this.filteredRows,
          columnsToExport,
          `${this.exportFilename}.${typeToExtensionMap[type]}`,
        );
        this.$emit('exported', { rows: this.filteredRows, type });
      } finally {
        this.$q.loading.hide();
      }
    },

    loadSettings() {
      if (this.externalSelectedColumns === null) {
        this.selectedColumns = this.defaultColumns || [];
      }
      if (this.saveFilterId === null) {
        return;
      }
      if (this.externalSelectedColumns === null) {
        const selectedColumns = localStorage.getItem(`${this.saveFilterId}Columns`);
        if (selectedColumns !== null) {
          this.selectedColumns = JSON.parse(selectedColumns);
        }
      }

      //
      if (this.externalFilters === null) {
        if (this.saveFilterId !== null) {
          const filterJSON = localStorage.getItem(`${this.saveFilterId}Filter`);

          this.filters = this.jsonParseFilters(filterJSON, this.columns);
        } else {
          this.filters = {};
        }

        if (isEmpty(this.filters)) {
          this.filters = this.defaultFilters;
        }
      }
    },

    saveFilter() {
      if (this.saveFilterId !== null) {
        localStorage.setItem(`${this.saveFilterId}Filter`, JSON.stringify(this.filters));
      }
    }, // saveFilter()

    saveSelectedColumns() {
      if (isArray(this.selectedColumns) && (this.saveFilterId !== null)) {
        localStorage.setItem(`${this.saveFilterId}Columns`, JSON.stringify(this.selectedColumns));
      }

      this.$emit('update:selectedColumns', this.selectedColumns);
    }, // saveSelectedColumns()

    clearSelection() {
      const selectedDisabledOnly = this.selected.filter((row) => this.isRowDisabled(row));
      this.$emit('update:selected', selectedDisabledOnly);
    }, // clearSelection()

    isRowDisabled(row) {
      if (this.disabledRows.length === 0) {
        return false;
      }
      return this.disabledRows.some((disabledRow) => (disabledRow[this.rowKey] === row[this.rowKey]));
    },

    getDisabledRowTooltip(row) {
      return this.disabledRowTooltipFn?.(row) || this.disabledRowTooltip;
    }, // getDisabledRowTooltip()

    getRowClass(row) {
      if (this.isRowDisabled(row)) {
        return 'cursor-not-allowed disabled';
      }
      return '';
    }, // getRowClass()

  },

};
</script>
